<template>
  <auth-layout title="Sign Up">
    <div class="mx-auto w-full max-w-90">
      <div>
        <h1 class="text-3xl font-semibold text-gray-900 mb-3">
          {{ t("auth.signup.heading") }}
        </h1>
        <p class="text-tertiary text-base">
          {{ t("auth.signup.subtitle") }}
        </p>
      </div>

      <form @submit.prevent="submit" class="mt-10 grid grid-cols-1 gap-y-5">
        <div class="grid sm:grid-cols-2 gap-5">
          <div>
            <label
              for="first_name"
              class="mb-2 block text-sm font-medium text-secondary"
              >{{ t("auth.signup.first_name") }}</label
            ><input
              id="first_name"
              type="text"
              name="first_name"
              autocomplete="first_name"
              v-model="form.first_name"
              class="block w-full appearance-none rounded-sm border border-gray-200 py-2.5 px-3.5 placeholder-gray-400 focus:outline-none"
              :placeholder="t('auth.placeholders.first_name')"
            />
            <p
              v-if="form.errors.first_name"
              class="mt-2 text-sm text-red-600"
              id="email-error"
            >
              {{ form.errors.first_name }}
            </p>
          </div>
          <div>
            <label
              for="last_name"
              class="mb-2 block text-sm font-medium text-secondary"
              >{{ t("auth.signup.last_name") }}</label
            ><input
              id="last_name"
              type="text"
              name="last_name"
              autocomplete="last_name"
              v-model="form.last_name"
              class="block w-full appearance-none rounded-sm border border-gray-200 py-2.5 px-3.5 placeholder-gray-400 focus:outline-none"
              :placeholder="t('auth.placeholders.last_name')"
            />
            <p
              v-if="form.errors.last_name"
              class="mt-2 text-sm text-red-600"
              id="email-error"
            >
              {{ form.errors.last_name }}
            </p>
          </div>
        </div>
        <div class="">
          <label
            for="company_name"
            class="mb-2 block text-sm font-medium text-secondary"
            >{{ t("auth.signup.company_name") }}</label
          ><input
            id="company_name"
            type="text"
            name="company_name"
            autocomplete="company_name"
            v-model="form.company_name"
            class="block w-full appearance-none rounded-sm border border-gray-200 py-2.5 px-3.5 placeholder-gray-400 focus:outline-none"
            :placeholder="t('auth.placeholders.company')"
          />
          <p
            v-if="form.errors.company_name"
            class="mt-2 text-sm text-red-600"
            id="email-error"
          >
            {{ form.errors.company_name }}
          </p>
        </div>
        <div class="">
          <PhoneInputWithLabel
            :label="t('auth.signup.phone_number')"
            v-model="form.phone"
            id="phone"
            :placeholder="t('auth.placeholders.phone')"
          />
          <p
            v-if="form.errors.phone"
            class="mt-2 text-sm text-red-600"
            id="email-error"
          >
            {{ form.errors.phone }}
          </p>
        </div>
        <div class="">
          <label
            for="email"
            class="mb-2 block text-sm font-medium text-secondary"
            >{{ t("auth.signup.email") }}</label
          ><input
            id="email"
            type="email"
            name="email"
            autocomplete="email"
            v-model="form.email"
            class="block w-full appearance-none rounded-sm border border-gray-200 py-2.5 px-3.5 placeholder-gray-400 focus:outline-none"
            :placeholder="t('auth.placeholders.email')"
          />
          <p
            v-if="form.errors.email"
            class="mt-2 text-sm text-red-600"
            id="email-error"
          >
            {{ form.errors.email }}
          </p>
        </div>
        <div class="">
          <label
            for="password"
            class="mb-2 block text-sm font-medium text-secondary"
            >{{ t("auth.signup.password") }}</label
          ><input
            id="password"
            type="password"
            name="password"
            autocomplete="password"
            v-model="form.password"
            class="block w-full appearance-none rounded-sm border border-gray-200 py-2.5 px-3.5 placeholder-gray-400 focus:outline-none"
            :placeholder="t('auth.placeholders.password')"
          />
          <p
            v-if="form.errors.password"
            class="mt-2 text-sm text-red-600"
            id="password-error"
          >
            {{ form.errors.password }}
          </p>
        </div>
        <TermsAndPrivacy class="mt-2" :label="t('auth.signup.submit')" />
        <primary-button
          class="w-full rounded-lg bg-dark-blue-700 focus:outline-none p-4 text-white font-semibold text-base capitalize"
          :loading="isSubmitting"
          type="submit"
          :label="t('auth.signup.submit')"
        >
        </primary-button>
      </form>
      <div class="flex justify-center items-center gap-1 mt-8">
        <span class="text-sm text-tertiary">{{
          t("auth.signup.have_an_account")
        }}</span>
        <Link
          :href="route('login')"
          class="font-medium text-dark-blue-700 hover:underline text-sm"
        >
          {{ t("auth.signup.login") }}
        </Link>
      </div>
    </div>
  </auth-layout>
</template>

<script setup>
import AuthLayout from "@/Tailwind/AuthLayout";
import { useForm, usePage, Link } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useWebView } from "@/Tailwind/Mixins/useWebView";
import { useBrowserLocale } from "@/Tailwind/Mixins/useBrowserLocale";
import { inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import TermsAndPrivacy from "@/Tailwind/Components/TermsAndPrivacy.vue";
import PhoneInputWithLabel from "@/Components/Inputs/PhoneInputWithLabel.vue";

const i18n = useI18n();
const t = i18n.t;

const defaultLocale = usePage().props.locale;
const browserLocale = useBrowserLocale(i18n, defaultLocale);

const { runningInWebView, requestMessagingDetails } = useWebView();

onMounted(() => {
  // Set the current language as the prefered browser language
  i18n.locale.value = browserLocale;
});

const emitter = inject("emitter");

const form = useForm({
  first_name: null,
  last_name: null,
  company_name: null,
  phone: null,
  country_code: null,
  email: null,
  password: null,
  locale: browserLocale,
});

const isSubmitting = ref(false);
const submit = () => {
  form.post(route("sign-up.store"), {
    onSuccess: () => {
      emitter.emit("authenticated");

      if (runningInWebView()) {
        requestMessagingDetails();
      }
    },
    onStart: () => {
      isSubmitting.value = true;
    },
    onError: () => {
      isSubmitting.value = false;
    },
  });
};
</script>

<style lang="scss">
.baseinput-core {
  @apply border border-gray-200 bg-gray-50 #{!important};

  input {
    @apply border-transparent focus:border-transparent focus:ring-0 text-sm;
  }
}

.lbgm-phone-scrll div {
  @apply hover:bg-gray-50;
}
</style>
